import React from 'react';

import { TableSortLabel } from '@mui/material';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  LoadedContent,
  Checkbox,
  LinearProgress,
} from '@occc-app/design-system';
import { LocationSortBy, SiteManagementLocationFragment } from '@occc/graphql';
import { useGetSiteManagementLocations } from '@occc-app/dataAccessLayer/location/queries';
import { SortingValue } from '@occc-app/hooks/useSortingParams';

import LocationListItem from './LocationListItem';
import { LocationListContainerProps } from './types';

export type LocationListProps = Omit<LocationListContainerProps, 'filter'> & {
  locationsData: ReturnType<typeof useGetSiteManagementLocations>;
  renderEmpty?: () => JSX.Element;
  sortingValue?: SortingValue | null;
  onToggleSort?: (field: LocationSortBy) => void;
};

const LocationList: React.FC<LocationListProps> = ({
  locationsData,
  isBulkSelected,
  bulkSelectedItems,
  handleBulkSelectAllClick,
  handleBulkSelect,
  getAdditionalRowMenuOptions,
  loading,
  isUserPermittedToEdit,
  renderEmpty,
  sortingValue,
  onToggleSort,
}) => {
  const { loading: locationsLoading, error, data, paginationProps } = locationsData;

  const renderSortableHeader = (field: LocationSortBy, label: string) => {
    if (!onToggleSort) {
      return label;
    }

    const isActive = sortingValue?.field === field;

    return (
      <TableSortLabel
        active={isActive}
        direction={isActive ? sortingValue.direction : undefined}
        onClick={() => onToggleSort(field)}
      >
        {label}
      </TableSortLabel>
    );
  };

  const onSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const selected = data?.map((item: { uuid: string }) => item.uuid) || [];
      return handleBulkSelectAllClick(selected);
    }

    return handleBulkSelectAllClick([]);
  };

  const renderData = (items: SiteManagementLocationFragment[]) => {
    const itemsCount = data?.length || 0;
    const indeterminate = bulkSelectedItems.length > 0 && bulkSelectedItems.length < itemsCount;
    const checked = itemsCount > 0 && bulkSelectedItems.length === itemsCount;

    return (
      <>
        {(loading || locationsLoading) && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              {isUserPermittedToEdit && (
                <TableCell padding='checkbox'>
                  <Checkbox
                    color='primary'
                    indeterminate={indeterminate}
                    checked={checked}
                    onChange={onSelectAllClick}
                    inputProps={{
                      'aria-label': 'select all',
                    }}
                  />
                </TableCell>
              )}
              <TableCell>{renderSortableHeader(LocationSortBy.SiteId, 'Site ID')}</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>
                {renderSortableHeader(
                  LocationSortBy.LastMobileConnection,
                  'Last Mobile Connection',
                )}
              </TableCell>
              <TableCell>
                {renderSortableHeader(
                  LocationSortBy.LastLoyaltyConnection,
                  'Last Loyalty Connection',
                )}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((location) => (
              <LocationListItem
                location={location}
                key={location.uuid}
                isItemSelected={isBulkSelected(location.uuid)}
                handleBulkSelect={handleBulkSelect}
                additionalMenuOptions={getAdditionalRowMenuOptions?.(location)}
                isUserPermittedToEditLocation={isUserPermittedToEdit}
              />
            ))}
          </TableBody>
        </Table>
        <Pagination
          {...paginationProps}
          requestNextPage={() => {
            handleBulkSelectAllClick([]);
            return paginationProps.requestNextPage();
          }}
          requestPreviousPage={() => {
            handleBulkSelectAllClick([]);
            return paginationProps.requestPreviousPage();
          }}
        />
      </>
    );
  };

  return (
    <LoadedContent<SiteManagementLocationFragment>
      error={error}
      loading={locationsLoading}
      items={data}
      renderData={renderData}
      renderEmpty={renderEmpty}
    />
  );
};

export default LocationList;
